/**
 * The class, as a row of faces.
 *
 * This is the one piece of the module experience that is about *people* rather
 * than about the playlist, and it is what makes a course something to take on: a
 * learner who can see three faces and a number knows exactly who they are behind.
 * The shape is the one every product that has ever shown "the people in this
 * thing" arrived at — overlapping circles, then a count — because it is the
 * smallest honest answer to *how many, and who*.
 *
 * Four decisions:
 *
 *  - **A ring, not a gap.** Faces overlap by a third and each carries a ring in
 *    the surface colour, so a row of seven photographs reads as seven people
 *    rather than as one smear. On a dark theme the ring is the card's own
 *    background, which is why it is a prop and not a constant — and the caller
 *    that is right about it is the one that *knows* the surface: the class panel
 *    hands down its own colour, and the door the card's, because a ring cut from
 *    the page behind a tinted card outlines every face in the wrong colour.
 *  - **The reader is in the row, first.** They are one of the people who played;
 *    a strip that left them out would be a strip of strangers, which is the
 *    opposite of the point (see `moduleAudienceFaces`).
 *  - **A face with no picture is still a face.** The app's own avatar draws its
 *    default person, so a class that has uploaded nothing still shows a class —
 *    and the strip never waits for a request per face.
 *  - **The last bubble is a number, and only a number.** `+6` is a fact; the fifth
 *    face at 26px is a smudge. The bubbles are already one *set* — overlapping
 *    circles with the count at the end — and the parentheses that used to bracket
 *    that set did the opposite of what they meant: read at a glance, `(+6)` looks
 *    like a seventh bubble whose contents are a number, when the whole point is
 *    that the row has stopped counting faces and started counting people.
 *  - **The strip is the way to the results.** A row of faces is where a reader
 *    tries to press when they want to know who those people are, so when the
 *    caller hands it an `onPress` the whole row becomes the door to the class's
 *    board — the same door the row was sitting beside before (see
 *    `ModuleAudience`, which used to draw the faces twice, once as people and once
 *    as a button, and used to draw none at all for a teacher).
 */
import {Pressable, StyleSheet, View} from 'react-native'
import {Trans} from '@lingui/react/macro'

import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {UserAvatar} from '#/view/com/util/UserAvatar'

/** How big one face is. A finger wide enough to be a face, small enough for six. */
const FACE = 30

/** How far each face sits over the one before it. */
const OVERLAP = 10

export function ModulePeerStack({
  faces,
  /** How many beyond the drawn faces — the *+N* bubble, when there is one. */
  others = 0,
  ringColor,
  onPress,
  accessibilityLabel,
  accessibilityHint,
  testID,
}: {
  /**
   * Who to draw, in order.
   *
   * Deliberately narrower than a board row: a face is a *did* and a picture, and
   * the one caller that has no board row to hand over — the first player's own
   * face (`ModuleAudience`) — should not have to invent a rank and a score to be
   * allowed into the row.
   */
  faces: readonly {did: string; avatar?: string}[]
  others?: number
  /**
   * What the rings are cut from: the colour of the surface the row sits on.
   *
   * Defaults to the page — right when the row is drawn straight on one, wrong on
   * anything tinted, so a caller on a coloured surface passes its own colour (see
   * `ModuleAudience`, which sits on the card's tinted band in a feed).
   */
  ringColor?: string
  /**
   * What pressing the row does — open the class's results, when a caller says so.
   *
   * Absent on a row that is nobody's door (the module's own page draws one too,
   * and its board is a screen below it), which is why this is optional rather than
   * a callback every caller has to remember to pass.
   */
  onPress?: () => void
  /** What a screen reader says this row is, when it is pressable. */
  accessibilityLabel?: string
  accessibilityHint?: string
  testID?: string
}) {
  const t = useTheme()
  const ring = ringColor ?? t.atoms.bg.backgroundColor

  if (faces.length === 0 && others === 0) return null

  const row = (
    <View
      testID={testID}
      style={[a.flex_row, a.align_center]}
      accessibilityRole="none">
      {faces.map((face, index) => (
        <View
          key={face.did}
          style={[
            styles.slot,
            {
              // Stacked left to right: each face steps over the one before it.
              marginLeft: index === 0 ? 0 : -OVERLAP,
              // The first face has nothing over it, so it needs no ring.
              zIndex: faces.length - index,
              backgroundColor: ring,
            },
          ]}>
          <UserAvatar type="user" size={FACE - 4} avatar={face.avatar} />
        </View>
      ))}

      {others > 0 ? (
        <View
          style={[
            styles.slot,
            styles.count,
            t.atoms.bg_contrast_100,
            {marginLeft: faces.length === 0 ? 0 : -OVERLAP},
          ]}>
          <Text
            testID={testID ? `${testID}-others` : undefined}
            numberOfLines={1}
            style={[a.text_xs, a.font_bold, t.atoms.text]}>
            <Trans>+{others}</Trans>
          </Text>
        </View>
      ) : null}
    </View>
  )

  if (!onPress) return row

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      onPress={onPress}
      style={({pressed}) => [pressed && styles.pressed]}>
      {row}
    </Pressable>
  )
}

const styles = StyleSheet.create({
  slot: {
    width: FACE,
    height: FACE,
    borderRadius: FACE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {
    width: 'auto',
    minWidth: FACE,
    paddingHorizontal: 8,
  },
  pressed: {
    opacity: 0.7,
  },
})
